import apiClient from './apiClient';

// Use the main apiClient which has proper error handling
const api = apiClient;

// Payroll Run API functions
export const payrollRunApi = {
  // Get all payroll runs
  getPayrollRuns: async (params = {}) => {
    try {
      const response = await api.get('/payroll/payroll-runs', { params });
      const raw = response.data || {};

      let runs = [];
      let pagination = null;

      if (Array.isArray(raw?.data?.payrollRuns)) {
        runs = raw.data.payrollRuns;
        pagination = raw.data.pagination || raw.pagination || null;
      } else if (Array.isArray(raw?.data?.runs)) {
        runs = raw.data.runs;
        pagination = raw.data.pagination || null;
      } else if (Array.isArray(raw?.data)) {
        runs = raw.data;
      } else if (Array.isArray(raw?.payrollRuns)) {
        runs = raw.payrollRuns;
        pagination = raw.pagination || null;
      } else if (Array.isArray(raw)) {
        runs = raw;
      }

      return {
        responseCode: raw.responseCode ?? response.status ?? 200,
        responseMessage: raw.responseMessage || raw.message || 'Payroll runs fetched successfully',
        data: runs,
        pagination,
        raw,
      };
    } catch (error) {
      throw error;
    }
  },

  // Get payroll run by ID
  getPayrollRunById: async (id) => {
    try {
      const response = await api.get(`/payroll/payroll-runs/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get detailed payroll run data for preview
  getPayrollRunDetails: async (id) => {
    try {
      const response = await api.get(`/payroll/payroll-runs/${id}/details`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Create new payroll run
  createPayrollRun: async (runData) => {
    try {
      const response = await api.post('/payroll/payroll-runs', runData);
      return response.data;
    } catch (error) {
      // Re-throw the error so it reaches the mutation's onError handler
      throw error;
    }
  },

  // Update payroll run
  updatePayrollRun: async (id, runData) => {
    try {
      const response = await api.put(`/payroll/payroll-runs/${id}`, runData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete payroll run
  deletePayrollRun: async (id) => {
    try {
      const response = await api.delete(`/payroll/payroll-runs/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Process payroll run
  processPayrollRun: async (id) => {
    try {
      const response = await api.post(`/payroll/payroll-runs/${id}/process`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Lock payroll run
  lockPayrollRun: async (id) => {
    try {
      const response = await api.post(`/payroll/payroll-runs/${id}/lock`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Unlock payroll run
  unlockPayrollRun: async (id) => {
    try {
      const response = await api.post(`/payroll/payroll-runs/${id}/unlock`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Generate payslips for payroll run
  generatePayslips: async (id) => {
    try {
      const response = await api.post(`/payroll/payroll-runs/${id}/generate-payslips`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Export payroll run data
  exportPayrollRun: async (id, format = 'excel') => {
    try {
      const response = await api.get(`/payroll/payroll-runs/${id}/export`, {
        params: { format },
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get payroll run statistics
  getPayrollRunStats: async (params = {}) => {
    try {
      const response = await api.get('/payroll/payroll-runs/stats', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Approve and Lock APIs
  approvePayrollRun: async (id) => {
    try {
      const response = await api.post(`/payroll/payroll-runs/${id}/approve`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  lockPayrollRun: async (id) => {
    try {
      const response = await api.post(`/payroll/payroll-runs/${id}/lock`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  unlockPayrollRun: async (id) => {
    try {
      const response = await api.post(`/payroll/payroll-runs/${id}/unlock`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Dashboard APIs
  getDashboardData: async () => {
    try {
      const response = await api.get('/payroll/dashboard');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getRecentActivities: async (params = {}) => {
    try {
      const response = await api.get('/payroll/activities', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getExceptions: async (params = {}) => {
    try {
      const response = await api.get('/payroll/exceptions', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get pay groups for dropdown
  getPayGroups: async (params = {}) => {
    try {
      const response = await api.get('/payroll/pay-groups', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get payroll statistics
  getPayrollStats: async () => {
    try {
      const response = await api.get('/payroll/stats');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get quick stats for dashboard (latest 5 items for each section)
  getQuickStats: async () => {
    try {
      const response = await api.get('/payroll/dashboard/quick-stats');
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

export default payrollRunApi;
